import { ShoppingBasket, Check, Plus, Star, MapPin, Truck, Clock, Home, Search, ShoppingCart, CreditCard, Package, Zap } from 'lucide-react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';

const OrderingMadeSimple = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    { icon: Search, title: "Browse thousands of products", desc: "Find what you need instantly" },
    { icon: ShoppingCart, title: "Add items with one tap", desc: "Simple and quick selection" },
    { icon: CreditCard, title: "Quick checkout process", desc: "Secure payment in seconds" },
    { icon: Package, title: "Track your order live", desc: "Real-time delivery updates" },
    { icon: Check, title: "Enjoy fresh deliveries", desc: "Quality guaranteed" }
  ];

  const animationSequence = [
    'logo',
    'home',
    'addToCart',
    'cart',
    'checkout',
    'tracking',
    'success'
  ];

  useEffect(() => {
    if (!isInView) return;
    
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % animationSequence.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isInView]);

  const renderPhoneContent = () => {
    switch (animationSequence[currentStep]) {
      case 'logo':
        return (
          <motion.div
            key="logo"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="flex items-center justify-center h-full bg-gradient-to-br from-red-500 via-red-600 to-orange-500"
          >
            <div className="text-center">
              <motion.div
                className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, duration: 0.8, type: "spring" }}
              >
                <span className="text-red-500 text-xl font-bold">D</span>
              </motion.div>
              <motion.h1 
                className="text-xl font-bold text-white mb-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                Dailzy
              </motion.h1>
              <motion.p 
                className="text-white/90 text-xs font-medium"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                Quick Commerce Platform
              </motion.p>
            </div>
          </motion.div>
        );

      case 'home':
        return (
          <motion.div
            key="home"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="h-full bg-gradient-to-b from-gray-50 to-white p-3"
          >
            {/* Header */}
            <motion.div 
              className="flex items-center justify-between mb-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="flex items-center">
                <div className="w-5 h-5 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-white text-xs font-bold">D</span>
                </div>
                <span className="font-bold text-sm text-gray-800">Dailzy</span>
              </div>
              <motion.div 
                className="w-6 h-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-sm"
                whileHover={{ scale: 1.1 }}
              >
                <ShoppingCart className="w-3 h-3 text-white" />
              </motion.div>
            </motion.div>
            
            {/* Search */}
            <motion.div 
              className="bg-white rounded-xl p-2.5 mb-3 shadow-sm border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="flex items-center text-gray-400 text-xs">
                <Search className="w-3 h-3 mr-2" />
                <span>Search for groceries, fruits...</span>
              </div>
            </motion.div>

            {/* Categories */}
            <motion.div 
              className="flex gap-2 mb-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {["🥬", "🍎", "🥛", "🍞"].map((emoji, index) => (
                <motion.div 
                  key={index}
                  className="flex-1 bg-white rounded-lg p-2 text-center shadow-sm border border-gray-50"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                >
                  <div className="text-lg mb-1">{emoji}</div>
                  <div className="text-xs text-gray-600">Fresh</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Featured Products */}
            <motion.div 
              className="space-y-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <div className="text-xs font-semibold text-gray-800 mb-2">🔥 Popular Today</div>
              {[
                { emoji: "🍎", name: "Fresh Red Apples", price: "₹250", discount: "20% OFF" },
                { emoji: "🥛", name: "Farm Fresh Milk", price: "₹60", discount: "₹10 OFF" }
              ].map((product, index) => (
                <motion.div 
                  key={index}
                  className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-50 flex items-center"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                >
                  <div className="text-2xl mr-3">{product.emoji}</div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-800">{product.name}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-red-500">{product.price}</span>
                      <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">{product.discount}</span>
                    </div>
                  </div>
                  <motion.button 
                    className="w-6 h-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center"
                    whileTap={{ scale: 0.9 }}
                  >
                    <Plus className="w-3 h-3 text-white" />
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        );

      case 'addToCart':
        return (
          <motion.div
            key="addToCart"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="h-full bg-gradient-to-b from-gray-50 to-white p-3"
          >
            {/* Header */}
            <motion.div 
              className="flex items-center justify-between mb-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="flex items-center">
                <div className="w-5 h-5 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-white text-xs font-bold">D</span>
                </div>
                <span className="font-bold text-sm text-gray-800">Product Details</span>
              </div>
              <motion.div 
                className="w-6 h-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-sm"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.8, repeat: 1 }}
              >
                <span className="text-xs text-white font-bold">1</span>
              </motion.div>
            </motion.div>

            {/* Product Detail */}
            <motion.div 
              className="bg-white rounded-xl p-4 mb-3 shadow-sm border border-gray-50"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <div className="text-center">
                <motion.div 
                  className="text-4xl mb-3"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  🍎
                </motion.div>
                <h3 className="font-semibold text-sm text-gray-800 mb-1">Fresh Red Apples</h3>
                <p className="text-gray-500 text-xs mb-2">Premium Kashmir apples, 1kg pack</p>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="text-lg font-bold text-red-500">₹250</div>
                  <div className="text-xs text-gray-400 line-through">₹300</div>
                  <div className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">17% OFF</div>
                </div>
                
                {/* Quantity selector */}
                <div className="flex items-center justify-center gap-3 mb-3">
                  <button className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-600">-</span>
                  </button>
                  <span className="font-semibold text-sm">1 kg</span>
                  <button className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <Plus className="w-3 h-3 text-red-500" />
                  </button>
                </div>

                <motion.button
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-2.5 rounded-xl font-semibold text-sm shadow-lg"
                  whileTap={{ scale: 0.98 }}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ delay: 1, duration: 0.6 }}
                >
                  ✓ Added to Cart
                </motion.button>
              </div>
            </motion.div>

            {/* Similar products */}
            <div className="text-xs font-semibold text-gray-800 mb-2">You might also like</div>
            <div className="flex gap-2">
              {["🥭", "🍌"].map((emoji, index) => (
                <div key={index} className="flex-1 bg-white rounded-lg p-2 text-center shadow-sm border border-gray-50">
                  <div className="text-lg mb-1">{emoji}</div>
                  <div className="text-xs text-gray-600">Fresh</div>
                </div>
              ))}
            </div>
          </motion.div>
        );

      case 'cart':
        return (
          <motion.div
            key="cart"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6 }}
            className="h-full bg-gradient-to-b from-gray-50 to-white p-3"
          >
            {/* Header */}
            <motion.div 
              className="flex items-center justify-between mb-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="flex items-center">
                <div className="w-5 h-5 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-white text-xs font-bold">D</span>
                </div>
                <span className="font-bold text-sm text-gray-800">My Cart</span>
              </div>
              <motion.div 
                className="w-6 h-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-sm"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: 2 }}
              >
                <span className="text-xs text-white font-bold">3</span>
              </motion.div>
            </motion.div>

            {/* Cart Items */}
            <motion.div 
              className="space-y-2 mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {[
                { emoji: "🍎", name: "Fresh Red Apples", price: "₹250", qty: "1 kg" },
                { emoji: "🥛", name: "Farm Fresh Milk", price: "₹60", qty: "500ml" },
                { emoji: "🍞", name: "Wheat Bread", price: "₹35", qty: "1 loaf" }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-50 flex items-center"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
                >
                  <div className="text-2xl mr-3">{item.emoji}</div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-800">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.qty}</div>
                  </div>
                  <div className="text-xs font-bold text-red-500">{item.price}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Total */}
            <motion.div 
              className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-3 mb-3 border border-red-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Subtotal:</span>
                <span className="text-xs font-medium">₹345</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Delivery fee:</span>
                <span className="text-xs font-medium text-green-600">FREE</span>
              </div>
              <div className="border-t border-red-200 pt-1">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-800">Total:</span>
                  <span className="text-sm font-bold text-red-500">₹345</span>
                </div>
              </div>
            </motion.div>

            <motion.button
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-2.5 rounded-xl font-semibold text-sm shadow-lg"
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
            >
              Proceed to Checkout →
            </motion.button>
          </motion.div>
        );

      case 'checkout':
        return (
          <motion.div
            key="checkout"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.6 }}
            className="h-full bg-gradient-to-b from-blue-50 to-white p-3"
          >
            {/* Header */}
            <motion.div 
              className="text-center mb-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="flex items-center justify-center mb-1">
                <div className="w-5 h-5 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-white text-xs font-bold">D</span>
                </div>
                <h2 className="font-bold text-sm text-gray-800">Checkout</h2>
              </div>
            </motion.div>

            {/* Address */}
            <motion.div 
              className="bg-white rounded-lg p-3 mb-2 shadow-sm border border-gray-50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="flex items-start">
                <MapPin className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-semibold text-gray-800">Delivery Address</div>
                  <div className="text-xs text-gray-600">123 MG Road, Bangalore</div>
                </div>
              </div>
            </motion.div>

            {/* Payment */}
            <motion.div 
              className="bg-white rounded-lg p-3 mb-3 shadow-sm border border-gray-50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <CreditCard className="w-4 h-4 text-green-500 mr-2" />
                  <span className="text-xs font-semibold text-gray-800">UPI Payment</span>
                </div>
                <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">Secure</span>
              </div>
              <div className="text-xs text-gray-600">••••@paytm</div>
            </motion.div>

            {/* Order Summary */}
            <motion.div 
              className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 mb-3 border border-blue-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <div className="text-xs font-semibold text-gray-800 mb-2">Order Summary</div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">3 items</span>
                <span className="text-xs font-medium">₹345</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Delivery</span>
                <span className="text-xs font-medium text-green-600">FREE</span>
              </div>
              <div className="border-t border-blue-200 pt-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-800">Total</span>
                  <span className="text-xs font-bold text-blue-600">₹345</span>
                </div>
              </div>
            </motion.div>

            <motion.button
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2.5 rounded-xl font-semibold text-sm shadow-lg flex items-center justify-center"
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <Clock className="w-3 h-3 mr-1" />
              Place Order (10 min delivery)
            </motion.button>
          </motion.div>
        );

      case 'tracking':
        return (
          <motion.div
            key="tracking"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.6 }}
            className="h-full bg-gradient-to-b from-orange-50 to-white p-3"
          >
            {/* Header */}
            <motion.div 
              className="text-center mb-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="flex items-center justify-center mb-1">
                <div className="w-5 h-5 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-white text-xs font-bold">D</span>
                </div>
                <h2 className="font-bold text-sm text-gray-800">Order Tracking</h2>
              </div>
            </motion.div>

            {/* Order Status */}
            <motion.div 
              className="bg-white rounded-lg p-3 mb-3 shadow-sm border border-gray-50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-800">Order #DZ12345</div>
                <motion.div 
                  className="w-2 h-2 bg-orange-500 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              </div>
              <div className="text-xs text-orange-600 font-medium mb-1">On the way to you!</div>
              <div className="text-xs text-gray-500">Estimated delivery: 6 minutes</div>
            </motion.div>

            {/* Delivery Person */}
            <motion.div 
              className="bg-white rounded-lg p-3 mb-3 shadow-sm border border-gray-50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-400 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-xs font-bold">R</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-gray-800">Rahul - Delivery Partner</div>
                  <div className="text-xs text-gray-500">★ 4.9 • 500+ deliveries</div>
                </div>
                <motion.button 
                  className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center"
                  whileTap={{ scale: 0.9 }}
                >
                  <span className="text-green-600 text-xs">📞</span>
                </motion.button>
              </div>
            </motion.div>

            {/* Progress */}
            <motion.div 
              className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3 mb-3 border border-orange-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <div className="space-y-2">
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">Order confirmed</span>
                </div>
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">Picked up from store</span>
                </div>
                <div className="flex items-center text-xs">
                  <motion.div 
                    className="w-2 h-2 bg-orange-500 rounded-full mr-2"
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <span className="text-orange-600 font-medium">Out for delivery</span>
                </div>
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mr-2"></div>
                  <span className="text-gray-400">Delivered</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white rounded-lg p-3 border border-gray-50 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <Truck className="w-6 h-6 text-orange-500 mx-auto mb-1" />
              <div className="text-xs font-medium text-gray-800">Arriving in 6 minutes</div>
            </motion.div>
          </motion.div>
        );

      case 'success':
        return (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.8, ease: "backOut" }}
            className="h-full bg-gradient-to-br from-green-50 to-white p-3 flex flex-col items-center justify-center"
          >
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <motion.div
                className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, duration: 0.8, type: "spring" }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  ✓
                </motion.div>
              </motion.div>
              
              <motion.h2 
                className="text-lg font-bold text-gray-800 mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                Order Delivered!
              </motion.h2>
              
              <motion.p 
                className="text-sm text-gray-600 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                Fresh groceries delivered in 8 minutes
              </motion.p>

              <motion.div 
                className="bg-white rounded-lg p-3 shadow-sm border border-gray-50 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
              >
                <div className="text-xs text-gray-500 mb-1">Rate your experience</div>
                <div className="flex justify-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.div
                      key={star}
                      className="w-6 h-6 text-yellow-400"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.2 + star * 0.1, duration: 0.3 }}
                    >
                      ⭐
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.button
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-xl font-semibold shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                whileTap={{ scale: 0.98 }}
              >
                Order Again
              </motion.button>
            </motion.div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-br from-white via-gray-50 to-red-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-red-200 to-orange-200 rounded-full opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-orange-200 to-red-200 rounded-full opacity-10 blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left content - matching other sections */}
          <div className="text-center lg:text-left">
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Zap size={20} />
                <span className="font-semibold text-sm">Quick Commerce</span>
              </motion.div>
              
              <motion.h2 
                className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                viewport={{ once: true }}
              >
                Ordering made{" "}
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  simple
                </span>
              </motion.h2>
              
              <motion.p 
                className="text-lg text-gray-600 max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                viewport={{ once: true }}
              >
                Experience the most elegant way to shop for your daily needs. 
                From browsing to delivery, every step is crafted for your ultimate convenience.
              </motion.p>
            </motion.div>

            <motion.div 
              className="space-y-4 mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              viewport={{ once: true }}
            >
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-4 bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  whileHover={{ x: 5 }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <motion.button
                className="group relative bg-gradient-to-r from-red-500 via-red-600 to-orange-500 hover:from-red-600 hover:via-red-700 hover:to-orange-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-xl transition-all duration-300 overflow-hidden"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative z-10 flex items-center">
                  Start Shopping Now
                  <motion.span
                    className="ml-2"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    →
                  </motion.span>
                </span>
              </motion.button>
            </motion.div>
          </div>

          {/* Right content - Phone mockup */}
          <motion.div 
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <motion.div 
                className="w-72 h-[580px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                  {/* Status bar */}
                  <div className="bg-white px-6 py-3 flex justify-between items-center text-black relative z-20">
                    <span className="text-sm font-semibold">9:41</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-4 h-2 bg-black rounded-sm"></div>
                      <div className="w-4 h-2 bg-black rounded-sm"></div>
                      <div className="w-6 h-3 border-2 border-black rounded-sm">
                        <div className="w-full h-full bg-green-500 rounded-sm"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Phone content with animation */}
                  <div className="h-full relative">
                    <AnimatePresence mode="wait">
                      {renderPhoneContent()}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
              
              {/* Floating elements */}
              <motion.div 
                className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Star className="w-6 h-6 text-yellow-800" />
              </motion.div>
              
              <motion.div 
                className="absolute -bottom-4 -left-4 w-10 h-10 bg-green-400 rounded-full flex items-center justify-center shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 1 }}
              >
                <Zap className="w-5 h-5 text-green-800" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OrderingMadeSimple; 